import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import { memo, type ReactNode } from "react";
import { easings } from "@funnel-ui/motion";

export type ProgressIndicatorStyle = "numbered" | "dots" | "gradient_bar" | "hybrid" | "pill";

type ProgressIndicatorProps = {
  style?: ProgressIndicatorStyle;
  current: number;
  total: number;
  ariaLabel?: string;
  colorOverride?: string;
  className?: string;
  testId?: string;
};

type VariantProps = {
  current: number;
  total: number;
  ariaLabel: string;
  activeColor: string;
  isComplete: boolean;
};

const clamp = (n: number, lo: number, hi: number): number => Math.min(Math.max(n, lo), hi);

const completionPulseStyle = (isComplete: boolean, reduced: boolean) =>
  isComplete && !reduced ? { animation: "fc-pop 320ms cubic-bezier(0.32,0.72,0,1)" } : undefined;

const Numbered = ({ current, total, ariaLabel, isComplete }: VariantProps) => {
  const prefersReduced = useReducedMotion();
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={current}
      aria-label={ariaLabel}
      className="inline-flex items-center justify-center text-[13px] font-medium tabular-nums"
      style={{ color: "hsl(var(--hero-fg-muted))", letterSpacing: "-0.01em" }}
    >
      <span>Step</span>
      <span
        className="relative ml-1 inline-flex h-[1em] min-w-[1.5ch] items-center justify-center overflow-hidden align-middle"
        style={{ color: "hsl(var(--brand-bg-foreground))" }}
      >
        <AnimatePresence initial={false}>
          <m.span
            key={current}
            initial={{ y: prefersReduced ? 0 : "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: prefersReduced ? 0 : "-100%", opacity: 0 }}
            transition={{
              duration: prefersReduced ? 0 : 0.2,
              ease: easings.emphasized,
            }}
            className="absolute inset-0 inline-flex items-center justify-center"
            style={completionPulseStyle(isComplete, !!prefersReduced)}
          >
            {current}
          </m.span>
        </AnimatePresence>
      </span>
      <span className="opacity-50"> / {total}</span>
    </div>
  );
};

const Pill = ({ current, total, ariaLabel, activeColor, isComplete }: VariantProps) => {
  const prefersReduced = useReducedMotion();
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={current}
      aria-label={ariaLabel}
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium tabular-nums"
      style={{
        background: "hsl(var(--brand-bg-foreground) / 0.08)",
        color: "hsl(var(--brand-bg-foreground) / 0.7)",
      }}
    >
      <span
        aria-hidden="true"
        className="h-2 w-2 rounded-full"
        style={{ background: activeColor, ...completionPulseStyle(isComplete, !!prefersReduced) }}
      />
      <span
        className="relative inline-flex h-[1em] min-w-[1.5ch] items-center justify-center overflow-hidden"
        style={{ color: "hsl(var(--brand-bg-foreground))" }}
      >
        <AnimatePresence initial={false}>
          <m.span
            key={current}
            initial={{ y: prefersReduced ? 0 : "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: prefersReduced ? 0 : "-100%", opacity: 0 }}
            transition={{ duration: prefersReduced ? 0 : 0.2, ease: easings.emphasized }}
            className="absolute inset-0 inline-flex items-center justify-center"
          >
            {current}
          </m.span>
        </AnimatePresence>
      </span>
      <span style={{ color: "hsl(var(--brand-bg-foreground) / 0.5)" }}>/ {total}</span>
    </div>
  );
};

const Dots = ({ current, total, ariaLabel, activeColor, isComplete }: VariantProps) => {
  const prefersReduced = useReducedMotion();
  const visibleCount = total <= 12 ? total : 12;
  const filledCount = total <= 12 ? current : Math.round((current / total) * 12);
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={current}
      aria-label={ariaLabel}
      className="flex items-center justify-center gap-1.5"
      style={completionPulseStyle(isComplete, !!prefersReduced)}
    >
      <span className="sr-only">
        Step {current} of {total}
      </span>
      {Array.from({ length: visibleCount }).map((_, i) => {
        const filled = i < filledCount;
        return (
          <span
            key={i}
            aria-hidden="true"
            className="h-1.5 rounded-full"
            style={{
              width: filled ? 20 : 6,
              background: filled ? activeColor : "hsl(var(--brand-bg-foreground) / 0.18)",
              transition: "width 240ms cubic-bezier(0.32,0.72,0,1), background 200ms",
            }}
          />
        );
      })}
    </div>
  );
};

const GradientBar = ({ current, total, ariaLabel, activeColor, isComplete }: VariantProps) => {
  const prefersReduced = useReducedMotion();
  const pct = (current / Math.max(total, 1)) * 100;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={current}
      aria-label={ariaLabel}
      className="relative h-1.5 w-full overflow-hidden rounded-full"
      style={{
        background: "hsl(var(--brand-bg-foreground) / 0.10)",
        ...completionPulseStyle(isComplete, !!prefersReduced),
      }}
    >
      <div
        className="relative h-full rounded-full"
        style={{
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${activeColor} 0%, color-mix(in oklab, ${activeColor} 70%, transparent) 100%)`,
          transition: "width 320ms cubic-bezier(0.32,0.72,0,1)",
        }}
      >
        {!prefersReduced && pct > 0 && pct < 100 && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)",
              backgroundSize: "200% 100%",
              animation: "fc-shimmer 1.5s linear infinite",
              mixBlendMode: "overlay",
            }}
          />
        )}
      </div>
    </div>
  );
};

const Hybrid = (props: VariantProps) => (
  <div className="flex items-center gap-3">
    <div className="shrink-0">
      <Numbered {...props} />
    </div>
    <div className="min-w-0 flex-1">
      <GradientBar {...props} />
    </div>
  </div>
);

const renderVariant = (style: ProgressIndicatorStyle, variantProps: VariantProps): ReactNode => {
  if (style === "numbered") return <Numbered {...variantProps} />;
  if (style === "dots") return <Dots {...variantProps} />;
  if (style === "pill") return <Pill {...variantProps} />;
  if (style === "hybrid") return <Hybrid {...variantProps} />;
  return <GradientBar {...variantProps} />;
};

const ProgressIndicatorImpl = ({
  style = "gradient_bar",
  current,
  total,
  ariaLabel = "Onboarding progress",
  colorOverride,
  className,
  testId,
}: ProgressIndicatorProps) => {
  const safeCurrent = clamp(current, 0, total);
  const isComplete = total > 0 && safeCurrent >= total;
  const activeColor = colorOverride ?? "hsl(var(--brand-primary))";
  const variantProps: VariantProps = {
    current: safeCurrent,
    total,
    ariaLabel,
    activeColor,
    isComplete,
  };

  return (
    <div
      data-testid={testId}
      data-style={style}
      data-complete={isComplete || undefined}
      className={className}
    >
      {renderVariant(style, variantProps)}
    </div>
  );
};

export const ProgressIndicator = memo(ProgressIndicatorImpl);

export default ProgressIndicator;
